import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Tune as ForcedIcon,
} from '@mui/icons-material';
import MsLinkRow from './MsLinkRow';
import MsIssueDetails from './MsIssueDetails';
import { SeverityBadge } from './MsProductList';
import {
  MP_LABEL,
  groupLinks,
  issueCounts,
  linkMatchesQuery,
  linkStatsByMp,
  mpChipSx,
  ruPlural,
  sortIssues,
  typeLabel,
  visibleIssues,
} from './msProductsModel';

function draftFromDetail(detail) {
  return {
    min: detail?.min_stock ?? '',
    max: detail?.max_stock ?? '',
    plan: detail?.replenish_skip ? '__skip__' : (detail?.processing_plan_id || ''),
  };
}

function sameDraft(a, b) {
  return String(a.min) === String(b.min) && String(a.max) === String(b.max) && a.plan === b.plan;
}

function draftPatch(draft) {
  const toNum = (v) => (v === '' || v == null ? null : Number(v));
  const patch = { min_stock: toNum(draft.min), max_stock: toNum(draft.max) };
  if (draft.plan === '__skip__') return { ...patch, replenish_skip: 1, processing_plan_id: null };
  return { ...patch, replenish_skip: 0, processing_plan_id: draft.plan || null };
}

function Section({ title, subtitle, action, children, sectionRef }) {
  return (
    <Card variant="outlined" ref={sectionRef}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: subtitle ? 0.5 : 1.5 }}>
          <Typography variant="subtitle1">{title}</Typography>
          {action}
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>{subtitle}</Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function StatusChip({ detail }) {
  if (detail.ms_archived) return <Chip size="small" label="архив в МС" />;
  const counts = issueCounts(visibleIssues(detail));
  if (counts.error) return <Chip size="small" color="error" label={ruPlural(counts.error, 'ошибка', 'ошибки', 'ошибок')} />;
  if (counts.warning) return <Chip size="small" color="warning" label={ruPlural(counts.warning, 'предупреждение', 'предупреждения', 'предупреждений')} />;
  if (counts.info) return <Chip size="small" color="info" variant="outlined" label={ruPlural(counts.info, 'подсказка', 'подсказки', 'подсказок')} />;
  return <Chip size="small" color="success" label="ок" />;
}

function ProblemsBanner({ detail, labels, onMore }) {
  const issues = visibleIssues(detail).filter((i) => i.severity === 'error' || i.severity === 'warning');
  if (!issues.length) return null;
  const severity = issues.some((i) => i.severity === 'error') ? 'error' : 'warning';
  const names = sortIssues(issues).map((i) => `${labels?.[i.code]?.label || i.code}${i.count > 1 ? ` ×${i.count}` : ''}`);
  return (
    <Alert severity={severity} action={<Button color="inherit" size="small" onClick={onMore}>Подробнее</Button>}>
      {names.join(', ')}
    </Alert>
  );
}

function LinksSection({ detail, labels, query, actions, busy }) {
  const groups = groupLinks(detail.links);
  const hasLinks = detail.links.length > 0;
  const broadcastOn = detail.links.some((l) => l.enabled);
  return (
    <Section
      title="Связи с маркетплейсами"
      subtitle="Трансляция — только передача остатков на МП. Режим остатка (живой / принуд / не транслировать) задаётся на товар и складскую связку кабинета."
      action={(
        <Stack direction="row" spacing={1} alignItems="center">
          {hasLinks && (
            <Tooltip title={broadcastOn
              ? 'Выключить трансляцию всех связей товара'
              : 'Включить трансляцию всех связей (кроме проблемных: архив, нет карточки, неактивный кабинет)'}
            >
              <Stack direction="row" alignItems="center">
                <Typography variant="caption" color="text.secondary">все</Typography>
                <Switch size="small" checked={broadcastOn} disabled={busy} onChange={(e) => actions.setBroadcastAll(e.target.checked)} />
              </Stack>
            </Tooltip>
          )}
          <Button size="small" startIcon={<AddIcon />} disabled={detail.ms_archived} onClick={() => actions.openLinkDialog(null)}>
            Добавить связь
          </Button>
        </Stack>
      )}
    >
      {!hasLinks && (
        <Typography variant="body2" color="text.secondary">Нет привязанных карточек МП</Typography>
      )}
      <Stack spacing={2}>
        {groups.map((group) => (
          <Stack key={group.marketplace} spacing={1.25}>
            {group.cabinets.map((cab) => (
              <Box key={cab.account_id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Chip size="small" label={MP_LABEL[group.marketplace] || group.marketplace} sx={{ ...mpChipSx(group.marketplace), height: 20 }} />
                  <Typography variant="body2" fontWeight={500}>{cab.account_name}</Typography>
                  {!cab.account_active && <Chip size="small" color="warning" variant="outlined" label="кабинет неактивен" sx={{ height: 20 }} />}
                  <Typography variant="caption" color="text.secondary">
                    {ruPlural(cab.links.length, 'карточка', 'карточки', 'карточек')}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {cab.links.map((link) => (
                    <MsLinkRow
                      key={`${link.marketplace}:${link.account_id}:${link.product_key}`}
                      link={link}
                      product={detail}
                      labels={labels}
                      highlighted={linkMatchesQuery(link, query)}
                      actions={actions}
                      busy={busy}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ))}
      </Stack>
    </Section>
  );
}

function ProblemsSection({ detail, labels, actions, busy, sectionRef }) {
  const linkBlocks = detail.links
    .filter((l) => (l.issues || []).length)
    .map((l) => ({ key: `${l.marketplace}:${l.account_id}:${l.product_key}`, link: l, issues: sortIssues(l.issues) }));
  const extra = [
    ...(detail.candidates || []).map((c, i) => ({
      key: `cand:${i}`,
      title: `Карточка без связи · ${MP_LABEL[c.marketplace] || c.marketplace} · ${c.account_name || c.account_id}`,
      issues: c.issues || [],
    })),
    ...(detail.forced_orphans || []).map((f, i) => ({
      key: `forced:${i}`,
      title: `Режим остатка на связке ${f.binding_name || f.binding_id}${f.account_name ? ` · ${f.account_name}` : ''}`,
      issues: f.issues || [],
    })),
  ];
  if (!linkBlocks.length && !extra.length) return null;
  const archivedWithLinks = detail.ms_archived && detail.links.length > 0;
  return (
    <Section title="Проблемы" sectionRef={sectionRef}>
      <Stack spacing={2} divider={<Divider flexItem />}>
        {archivedWithLinks && (
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Все связи товара ({detail.links.length})
            </Typography>
            <MsIssueDetails
              issue={{
                code: 'MS_ARCHIVED',
                severity: detail.links.some((l) => l.enabled) ? 'error' : 'warning',
                details: { target_ms: detail.rebind_targets?.[0] || null },
              }}
              labels={labels}
              product={detail}
              actions={actions}
              busy={busy}
            />
          </Box>
        )}
        {linkBlocks.map((block) => (
          <Box key={block.key}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {MP_LABEL[block.link.marketplace] || block.link.marketplace} · {block.link.account_name || block.link.account_id}
              {' · '}{block.link.article || block.link.product_key}
            </Typography>
            <Stack spacing={1.25}>
              {block.issues.map((issue) => (
                <Box key={issue.code}>
                  <Typography variant="caption" fontWeight={600} color={`${issue.severity}.main`}>
                    {labels?.[issue.code]?.label || issue.code}
                  </Typography>
                  <MsIssueDetails issue={issue} labels={labels} link={block.link} product={detail} actions={actions} busy={busy} />
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
        {extra.map((block) => (
          <Box key={block.key}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>{block.title}</Typography>
            {block.issues.map((issue) => (
              <MsIssueDetails key={issue.code} issue={issue} labels={labels} product={detail} actions={actions} busy={busy} />
            ))}
          </Box>
        ))}
      </Stack>
    </Section>
  );
}

function SettingsSection({ detail, onSave, saving }) {
  const savedKey = [detail.ms_id, detail.min_stock, detail.max_stock, detail.processing_plan_id, detail.replenish_skip].join('|');
  const saved = useMemo(() => draftFromDetail(detail), [savedKey]);
  const [draft, setDraft] = useState(saved);
  useEffect(() => { setDraft(saved); }, [saved]);
  const dirty = !sameDraft(draft, saved);
  const locked = !!detail.ms_archived;
  const minMaxPartial = (draft.min === '') !== (draft.max === '');
  return (
    <Section
      title="Настройки пополнения"
      action={(
        <Stack direction="row" spacing={1} alignItems="center">
          {dirty && <Chip size="small" color="warning" label="не сохранено" />}
          <Button size="small" onClick={() => setDraft(saved)} disabled={!dirty || saving}>Отменить</Button>
          <Button
            size="small"
            variant="contained"
            disabled={!dirty || saving || locked || minMaxPartial}
            onClick={() => onSave(draftPatch(draft))}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </Stack>
      )}
    >
      {locked && <Alert severity="info" sx={{ mb: 1.5 }}>Товар в архиве МС — настройки не редактируются</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          size="small"
          type="number"
          label="Min"
          value={draft.min}
          disabled={locked}
          onChange={(e) => setDraft((d) => ({ ...d, min: e.target.value }))}
          sx={{ width: { sm: 120 } }}
        />
        <TextField
          size="small"
          type="number"
          label="Max"
          value={draft.max}
          disabled={locked}
          onChange={(e) => setDraft((d) => ({ ...d, max: e.target.value }))}
          sx={{ width: { sm: 120 } }}
        />
        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} error={Boolean(detail.processing_plan_archived) && draft.plan !== '__skip__' && draft.plan === saved.plan}>
          <InputLabel shrink>Техкарта</InputLabel>
          <Select
            label="Техкарта"
            notched
            displayEmpty
            value={draft.plan}
            disabled={locked}
            onChange={(e) => setDraft((d) => ({ ...d, plan: e.target.value }))}
          >
            <MenuItem value="">Не выбрана</MenuItem>
            <MenuItem value="__skip__">Не производить</MenuItem>
            {(detail.techcards || []).map((plan) => (
              <MenuItem key={plan.id} value={plan.id} disabled={Boolean(plan.archived)}>
                {plan.name || plan.id}
                {plan.archived ? ' (архив, недоступна)' : ''}
                {plan.is_parametric && !plan.archived ? ' (параметрическая)' : ''}
                {plan.name_process ? ` · ${plan.name_process}` : ''}
              </MenuItem>
            ))}
          </Select>
          {detail.processing_plan_archived && !detail.replenish_skip && draft.plan === saved.plan ? (
            <FormHelperText>Сохранённая техкарта недоступна — выберите живую</FormHelperText>
          ) : null}
        </FormControl>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {minMaxPartial
          ? 'Min и max задаются вместе — заполните оба или очистите оба.'
          : 'Min/max и техкарта — на товаре МС. «Не производить» снимает и задание, и перемещение.'}
      </Typography>
    </Section>
  );
}

function StockSection({ detail, onLoad, loading }) {
  const known = detail.available !== undefined;
  return (
    <Section
      title="Остатки"
      action={(
        <Button size="small" onClick={onLoad} disabled={loading}>
          {known ? 'Обновить' : 'Показать доступно'}
        </Button>
      )}
    >
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Typography variant="body2">
        Доступно на складах ГП (производство + швейка):{' '}
        <strong>{known ? (detail.available == null ? '—' : detail.available) : 'не загружено'}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">Запрос в МойСклад, по нажатию.</Typography>
    </Section>
  );
}

function VariantsSection({ detail, onSelect }) {
  if (!detail.variants_count) return null;
  return (
    <Section title={`Модификации (${detail.variants_count})`}>
      <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
        {detail.variants.map((v) => (
          <ListItemButton key={v.ms_id} divider onClick={() => onSelect(v.ms_id)}>
            <ListItemText
              primary={v.name || v.ms_id}
              secondary={v.links.total ? `связей ${v.links.total}, трансляция ${v.links.enabled}` : 'нет связей'}
              slotProps={{
                primary: { noWrap: true, title: v.name || '', color: v.ms_archived ? 'text.disabled' : undefined },
                secondary: { noWrap: true },
              }}
            />
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
              {v.marketplaces.map((mp) => (
                <Box key={mp} sx={{ ...mpChipSx(mp), borderRadius: 1, px: 0.5, fontSize: 11, lineHeight: '18px' }}>
                  {MP_LABEL[mp] || mp}
                </Box>
              ))}
              <SeverityBadge issues={v.issues} />
            </Stack>
          </ListItemButton>
        ))}
      </List>
    </Section>
  );
}

export default function MsProductDetail({
  detail,
  loading,
  error,
  labels,
  query,
  busy,
  actions,
  savingSettings,
  stockLoading,
  onSaveSettings,
  onLoadStock,
}) {
  const problemsRef = useRef(null);

  if (!detail) {
    return (
      <Card variant="outlined">
        {loading && <LinearProgress />}
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            {error ? 'Не удалось загрузить товар' : (loading ? 'Загрузка…' : 'Выберите товар слева')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const stats = linkStatsByMp(detail.links);
  const copyId = () => {
    navigator.clipboard?.writeText(detail.ms_id).then(() => actions.notify.success('ms_id скопирован')).catch(() => {});
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap title={detail.name} color={detail.ms_archived ? 'text.secondary' : undefined}>
                {detail.name || detail.ms_id}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }} alignItems="center">
                <StatusChip detail={detail} />
                <Chip size="small" variant="outlined" label={typeLabel(detail.type)} />
                {stats.map((s) => (
                  <Chip
                    key={s.marketplace}
                    size="small"
                    label={`${MP_LABEL[s.marketplace]} ${s.enabled}/${s.total}`}
                    sx={{ ...mpChipSx(s.marketplace), opacity: s.enabled ? 1 : 0.5 }}
                  />
                ))}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ flexShrink: 0 }}>
              <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={detail.ms_archived} onClick={() => actions.openLinkDialog(null)}>
                Добавить связь
              </Button>
              <Button size="small" startIcon={<ForcedIcon />} onClick={actions.openForced}>По складам</Button>
              <Button size="small" startIcon={<SendIcon />} disabled={busy || !detail.links.length} onClick={actions.sendStocks}>
                Отправить остатки
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>ms_id</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{detail.ms_id}</Typography>
              <Tooltip title="Скопировать">
                <IconButton size="small" onClick={copyId} sx={{ p: 0.25 }}><CopyIcon sx={{ fontSize: 14 }} /></IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>ШК</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {(detail.barcodes || []).join(' / ') || '—'}
              </Typography>
            </Stack>
            {detail.parent && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Родитель</Typography>
                {detail.parent.missing ? (
                  <Typography variant="body2" color="text.disabled">нет в МС ({detail.parent.ms_id})</Typography>
                ) : (
                  <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => actions.selectProduct(detail.parent.ms_id)}>
                    {detail.parent.name || detail.parent.ms_id}{detail.parent.ms_archived ? ' (архив)' : ''}
                  </Button>
                )}
              </Stack>
            )}
            {detail.variants_count > 0 && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>Модификации</Typography>
                <Typography variant="body2">{detail.variants_count}</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
        {loading && <LinearProgress />}
      </Card>

      <ProblemsBanner
        detail={detail}
        labels={labels}
        onMore={() => problemsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />

      <LinksSection detail={detail} labels={labels} query={query} actions={actions} busy={busy} />
      <ProblemsSection detail={detail} labels={labels} actions={actions} busy={busy} sectionRef={problemsRef} />
      <SettingsSection detail={detail} onSave={onSaveSettings} saving={savingSettings} />
      <StockSection detail={detail} onLoad={onLoadStock} loading={stockLoading} />
      <VariantsSection detail={detail} onSelect={actions.selectProduct} />
    </Stack>
  );
}
